"use client";

import { useCallback, useEffect, useState } from "react";

type Signal = {
  dimension: string; value: number | null; confidence: number; evidenceCount: number;
  source: string; updatedAt: string; valuePercent: number | null; confidencePercent: number; scoreDisclosed: boolean;
};

type Version = {
  version: number; triggerType: string; triggerRoundId: string | null;
  modelConfidence: number; evidenceCount: number; createdAt: string;
  changes: {
    kind: string;
    fields?: Array<{ field: string; before: number; after: number; delta: number }>;
    capabilities?: Array<{ key: string; label: string; before: { score?: number; confidence?: number } | null; after: { score?: number; confidence?: number } }>;
  };
  snapshot: unknown;
};

type DebugPayload = {
  debug: { enabled: true; viewer: string; generatedAt: string; environment: string; readOnly: boolean };
  context: { account: Record<string, unknown> | null; learner: Record<string, unknown> | null; aspiration: Record<string, unknown> | null; journey: Record<string, unknown> | null };
  learnerModel: { signals: Signal[]; latestReport: unknown; latestVersion: number | null };
  history: { versions: Version[]; recentEvidence: Array<Record<string, unknown>>; assessmentSessions: Array<Record<string, unknown>> };
};

function dateTime(value: string) {
  return new Intl.DateTimeFormat("vi-VN", { dateStyle: "short", timeStyle: "medium", timeZone: "Asia/Ho_Chi_Minh" }).format(new Date(value));
}

function JsonBlock({ value }: { value: unknown }) {
  return <pre>{JSON.stringify(value, null, 2)}</pre>;
}

const signalDescriptions: Record<string, { label: string; meaning: string }> = {
  behavior_confidence: { label: "Sự tự tin khi học", meaning: "Mức độ learner tin vào khả năng của mình khi bắt đầu và trả lời một nhiệm vụ học tập." },
  behavior_curiosity: { label: "Sự tò mò", meaning: "Mức độ learner muốn khám phá, đặt câu hỏi và tìm hiểu thêm ngoài yêu cầu tối thiểu." },
  behavior_persistence: { label: "Khả năng kiên trì", meaning: "Khả năng tiếp tục thử khi gặp câu khó, sai đáp án hoặc chưa nhìn ra cách giải." },
  behavior_reflection: { label: "Khả năng nhìn lại", meaning: "Mức độ learner xem lại cách làm, nhận ra sai sót và rút kinh nghiệm sau một nhiệm vụ." },
  behavior_self_learning: { label: "Khả năng tự học", meaning: "Khả năng tự bắt đầu, lựa chọn cách học và hoàn thành việc học mà không cần nhắc liên tục." },
  challenge_comfort: { label: "Sự thoải mái với thử thách", meaning: "Mức độ learner sẵn sàng thử câu chưa chắc chắn và không quá sợ mắc lỗi." },
  english_grammar: { label: "Ngữ pháp tiếng Anh", meaning: "Mức độ thể hiện hiện tại ở các câu hỏi về cấu trúc câu và quy tắc ngữ pháp tiếng Anh." },
  learning_autonomy: { label: "Mức chủ động học tập", meaning: "Mức độ learner tự đưa ra lựa chọn và duy trì việc học mà không phụ thuộc hoàn toàn vào người hướng dẫn." },
  math_algebra: { label: "Đại số", meaning: "Mức độ thể hiện hiện tại ở biểu thức, biến số, phương trình và các quan hệ đại số." },
  math_arithmetic: { label: "Số học và tính toán", meaning: "Mức độ chính xác và linh hoạt khi thực hiện các phép tính cơ bản." },
  math_fraction: { label: "Phân số", meaning: "Mức độ hiểu và vận dụng so sánh, biến đổi cùng các phép tính với phân số." },
  math_geometry: { label: "Hình học", meaning: "Mức độ hiểu hình, quan hệ hình học, đo lường và lập luận hình học." },
  math_logical_reasoning: { label: "Lập luận logic Toán", meaning: "Khả năng nhận ra quy luật, kết nối dữ kiện và suy luận để đi tới kết luận." },
  math_number_sense: { label: "Cảm nhận về số", meaning: "Khả năng hiểu độ lớn, quan hệ và cách biểu diễn số thay vì chỉ thực hiện phép tính máy móc." },
  persistence: { label: "Sự kiên trì", meaning: "Mức độ learner duy trì nỗ lực qua các lựa chọn và tương tác gần đây." },
};

function signalInfo(dimension: string) {
  return signalDescriptions[dimension] ?? {
    label: dimension.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" "),
    meaning: "Một khía cạnh đang được hệ thống theo dõi từ các câu trả lời và hoạt động học tập gần đây.",
  };
}

function valueExplanation(value: number) {
  if (value >= 80) return "Đang thể hiện rõ và ổn định trong các lần quan sát hiện có.";
  if (value >= 60) return "Đã thể hiện khá rõ, nhưng vẫn còn không gian để củng cố.";
  if (value >= 40) return "Đang hình thành; cần thêm hoạt động để thấy xu hướng rõ hơn.";
  return "Chưa thể hiện nhiều trong dữ liệu hiện có; đây không phải kết luận cố định.";
}

function sourceExplanation(source: string) {
  if (source === "multi_round_assessment") return "bài đánh giá theo từng môn hoặc từng mảng";
  if (source === "adaptive_survey") return "các câu hỏi tìm hiểu được điều chỉnh theo câu trả lời";
  return source.replaceAll("_", " ");
}

export function LearnerModelDebugPanel() {
  const [payload, setPayload] = useState<DebugPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expandedSignal, setExpandedSignal] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/debug/learner-model", { cache: "no-store" })
      .then(async (response) => {
        if (response.status === 403) return null;
        if (!response.ok) throw new Error("DEBUG_UNAVAILABLE");
        return response.json() as Promise<DebugPayload>;
      })
      .then(setPayload)
      .catch(() => setPayload(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  if (!payload) return null;

  const latest = payload.history.versions[0];
  async function copyPayload() {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  if (!open) {
    return (
      <button className="lm-fab" type="button" onClick={() => setOpen(true)} title="Learner Model debug (test user)">
        <style>{LM_CSS}</style>
        🧪 Model v{payload.learnerModel.latestVersion ?? "—"}
      </button>
    );
  }

  return (
    <div className="lm-overlay" onClick={() => setOpen(false)}>
      <style>{LM_CSS}</style>
      <aside className="learner-debug lm-open" aria-label="Learner Model debug dành cho test user" onClick={(event) => event.stopPropagation()}>
      <header>
        <div>
          <span>TEST USER · READ ONLY · PRODUCTION</span>
          <h2>Learner Model Debug Console</h2>
          <p>{payload.debug.viewer} · sinh lúc {dateTime(payload.debug.generatedAt)}</p>
        </div>
        <div className="learner-debug-actions">
          <button onClick={() => setOpen(false)} type="button">✕ Thu gọn</button>
          <button onClick={load} type="button">↻ Làm mới</button>
          <button onClick={copyPayload} type="button">{copied ? "Đã copy" : "Copy JSON"}</button>
        </div>
      </header>

      <section className="learner-debug-kpis">
        <article><small>Model version</small><strong>{payload.learnerModel.latestVersion ?? "—"}</strong></article>
        <article><small>Model confidence</small><strong>{latest ? `${latest.modelConfidence}%` : "—"}</strong></article>
        <article><small>Evidence</small><strong>{latest?.evidenceCount ?? payload.history.recentEvidence.length}</strong></article>
        <article><small>Signals</small><strong>{payload.learnerModel.signals.length}</strong></article>
        <article><small>Assessment sessions</small><strong>{payload.history.assessmentSessions.length}</strong></article>
      </section>

      <section className="learner-debug-section">
        <div className="learner-debug-heading"><h3>Signals hiện tại</h3><span>Value · confidence · evidence</span></div>
        {payload.learnerModel.signals.length ? (
          <div className="learner-debug-signals">
            {payload.learnerModel.signals.map((signal) => {
              const expanded = expandedSignal === signal.dimension;
              const info = signalInfo(signal.dimension);
              return (
                <article
                  aria-expanded={expanded}
                  className={expanded ? "expanded" : ""}
                  key={signal.dimension}
                  onClick={() => setExpandedSignal(expanded ? null : signal.dimension)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setExpandedSignal(expanded ? null : signal.dimension);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="debug-signal-title">
                    <div><strong>{signal.dimension}</strong><span>{info.label}</span></div>
                    <small>{expanded ? "Thu gọn ↑" : "Xem chi tiết ↓"}</small>
                  </div>
                  <div className="debug-meter"><span style={{ width: `${signal.valuePercent ?? 0}%` }} /></div>
                  <dl><div><dt>Value</dt><dd>{signal.scoreDisclosed ? `${signal.valuePercent}%` : "Chưa công bố"}</dd></div><div><dt>Confidence</dt><dd>{signal.confidencePercent}%</dd></div><div><dt>Evidence</dt><dd>{signal.evidenceCount}</dd></div></dl>
                  <time>{dateTime(signal.updatedAt)}</time>
                  {expanded ? (
                    <div className="debug-signal-details">
                      <div><small>Ý nghĩa</small><p>{info.meaning}</p></div>
                      <div><small>Cách đọc kết quả</small><p>{signal.scoreDisclosed && signal.valuePercent !== null ? <><strong>{signal.valuePercent}%:</strong> {valueExplanation(signal.valuePercent)}</> : "Chưa công bố điểm vì độ chắc chắn hoặc lượng thông tin chưa đạt ngưỡng."}</p></div>
                      <div><small>Độ chắc chắn</small><p><strong>{signal.confidencePercent}%:</strong> mức hệ thống có thể tin vào kết quả này dựa trên lượng và độ đa dạng của dữ liệu.</p></div>
                      <div><small>Bằng chứng và nguồn</small><p><strong>{signal.evidenceCount} bằng chứng</strong> từ {sourceExplanation(signal.source)}.</p></div>
                      <p className="debug-signal-note">Kết quả sẽ thay đổi khi learner làm thêm bài đánh giá hoặc có hoạt động học tập mới.</p>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : <p className="learner-debug-empty">Chưa có signal. Hoàn thành diagnosis hoặc assessment để tạo evidence đầu tiên.</p>}
      </section>

      <section className="learner-debug-section">
        <div className="learner-debug-heading"><h3>Lịch sử thay đổi Learner Model</h3><span>{payload.history.versions.length} version</span></div>
        {payload.history.versions.length ? (
          <div className="learner-debug-timeline">
            {payload.history.versions.map((version) => (
              <article key={version.version}>
                <div className="debug-version"><span>v{version.version}</span><time>{dateTime(version.createdAt)}</time></div>
                <div>
                  <strong>{version.triggerRoundId || version.triggerType}</strong>
                  <p>Confidence {version.modelConfidence}% · {version.evidenceCount} evidence · {version.changes.kind}</p>
                  {(version.changes.fields ?? []).map((change) => <small key={change.field}>{change.field}: {change.before} → {change.after} ({change.delta > 0 ? "+" : ""}{change.delta})</small>)}
                  {(version.changes.capabilities ?? []).slice(0, 8).map((change) => <small key={change.key}>{change.label}: {change.before?.score ?? "mới"} → {change.after.score ?? "—"}</small>)}
                  <details><summary>Xem snapshot đầy đủ</summary><JsonBlock value={version.snapshot} /></details>
                </div>
              </article>
            ))}
          </div>
        ) : <p className="learner-debug-empty">Chưa có version. Mỗi assessment round hoàn thành sẽ tạo một snapshot bất biến tại đây.</p>}
      </section>

      <section className="learner-debug-raw">
        <details open><summary>Context: account, learner, aspiration, journey</summary><JsonBlock value={payload.context} /></details>
        <details><summary>Recent evidence ({payload.history.recentEvidence.length})</summary><JsonBlock value={payload.history.recentEvidence} /></details>
        <details><summary>Assessment sessions ({payload.history.assessmentSessions.length})</summary><JsonBlock value={payload.history.assessmentSessions} /></details>
        <details><summary>Latest Learner Report</summary><JsonBlock value={payload.learnerModel.latestReport} /></details>
      </section>
      </aside>
    </div>
  );
}

const LM_CSS = `
.lm-fab{position:fixed;left:16px;bottom:16px;z-index:9998;display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:999px;border:1px solid #2b3b57;background:rgba(15,26,46,.85);color:#cfe0ff;font-size:12.5px;font-weight:600;font-family:ui-monospace,SFMono-Regular,monospace;cursor:pointer;-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px)}
.lm-fab:hover{border-color:#8fd3ff}
.lm-overlay{position:fixed;inset:0;z-index:99999;background:rgba(5,9,16,.6);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);display:flex;align-items:flex-end;justify-content:center;overflow:auto}
.learner-debug.lm-open{position:relative!important;inset:auto!important;margin:0 auto!important;width:min(1080px,96vw)!important;max-height:92vh!important;overflow-y:auto!important;border-radius:18px 18px 0 0!important;animation:lm-up .22s ease}
@keyframes lm-up{from{transform:translateY(34px);opacity:.5}to{transform:translateY(0);opacity:1}}
`;
